export type CategoryVerificationSource = "loyverse_d1" | "d1_local";

export type VerifiedCategoryBulkResult = {
  ok: true;
  successes: Array<{
    item_id: string;
    verified_value: string | null;
    verification_source: CategoryVerificationSource;
  }>;
  failures: Array<{
    item_id: string;
    error: string;
    reconciliation_required: boolean;
  }>;
};

export type MalformedCategoryBulkResult = { ok: false; error: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Fail closed: category changes are successful only after this exact response contract. */
export function validateCategoryBulkResponse(
  response: unknown,
  { requestId, targets, categoryId }: { requestId: string; targets: readonly string[]; categoryId: string | null },
): VerifiedCategoryBulkResult | MalformedCategoryBulkResult {
  const malformed = (error: string): MalformedCategoryBulkResult => ({ ok: false, error });
  if (!isRecord(response)) return malformed("Phản hồi máy chủ không đúng định dạng");
  if (response.request_id !== requestId) return malformed("Mã yêu cầu trong phản hồi không khớp");
  if (response.submitted !== targets.length) return malformed("Số sản phẩm trong phản hồi không khớp");
  if (!Array.isArray(response.results) || response.results.length !== targets.length) return malformed("Kết quả cập nhật không đầy đủ");

  const targetIds = new Set(targets);
  if (targetIds.size !== targets.length || [...targetIds].some((id) => !id)) return malformed("Danh sách sản phẩm yêu cầu không hợp lệ");

  const seenIds = new Set<string>();
  const successes: VerifiedCategoryBulkResult["successes"] = [];
  const failures: VerifiedCategoryBulkResult["failures"] = [];
  for (const row of response.results) {
    if (!isRecord(row) || typeof row.item_id !== "string" || !targetIds.has(row.item_id) || seenIds.has(row.item_id)) {
      return malformed("Mã sản phẩm trong phản hồi không khớp");
    }
    seenIds.add(row.item_id);
    if (row.operation !== "category_set") return malformed("Thao tác trong phản hồi không khớp");
    if (row.verification_source !== "loyverse_d1" && row.verification_source !== "d1_local") {
      return malformed("Nguồn xác minh trong phản hồi không hợp lệ");
    }
    if (row.ok === true) {
      if (row.verified_value !== categoryId) {
        return malformed("Danh mục đã xác minh trong phản hồi không khớp");
      }
      successes.push({ item_id: row.item_id, verified_value: categoryId, verification_source: row.verification_source });
      continue;
    }
    if (
      row.ok !== false
      || typeof row.error !== "string"
      || !row.error.trim()
      || row.error.trim().length > 500
      || ("verified_value" in row && row.verified_value !== null && typeof row.verified_value !== "string")
      || ("reconciliation_required" in row && typeof row.reconciliation_required !== "boolean")
    ) {
      return malformed("Lỗi cập nhật trong phản hồi không hợp lệ");
    }
    failures.push({
      item_id: row.item_id,
      error: row.error.trim(),
      reconciliation_required: row.reconciliation_required === true,
    });
  }
  if (seenIds.size !== targetIds.size) return malformed("Phản hồi thiếu sản phẩm");
  return { ok: true, successes, failures };
}
